/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import React from 'react';

import { getMobileOperatingSystem } from './src/helpers';
import { setCookieTracking } from './src/helpers/setCookieTracking';
import {
  appendScriptTagManager,
  appendThirdPartyScriptsAsync,
  appendThirdPartyScriptsSync,
  handleScriptEqualWeb,
} from './src/helpers/thirdPartyScripts';
import { initVisitorTracking } from './src/helpers/visitorTracking';
import { singular, useSingularIdentify } from './src/lib/singular';
import 'plyr/dist/plyr.css';
import './src/styles/global.css';

export const onClientEntry = async () => {
  if (typeof IntersectionObserver === 'undefined') {
    await import('intersection-observer');
    console.log('IntersectionObserver polyfilled');
  }
};

export const onInitialClientRender = () => {
  initVisitorTracking();
  setCookieTrackingMain();

  // Defer ALL third-party scripts (analytics + observability + GTM) until the
  // first user interaction OR a 1-second fallback timer, whichever comes
  // first. Previously the "Sync" bundle (GTM, GA4, Google Ads, TikTok,
  // Reddit, Spotify, SwitchBoost, Amazon Ads, Singular) ran during initial
  // hydration, which added ~700 KB of script parsing to the LCP/TBT window.
  // Folding it into the same interaction-gated trigger that Sentry already
  // uses keeps tracking but moves the cost off the critical path.
  if (process.env.NODE_ENV === 'production') {
    const events = ['keydown', 'pointerover', 'touchstart', 'scroll'];
    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      events.forEach((e) => window.removeEventListener(e, fire));
      appendScriptTagManager();
      appendThirdPartyScriptsSync();
      appendThirdPartyScriptsAsync();
    };
    events.forEach((e) => window.addEventListener(e, fire, { once: true, passive: true }));
    setTimeout(fire, 1000);
  }
};

export const onRouteUpdate = ({ location, prevLocation }) => {
  if (prevLocation) {
    singular.pageVisit();
  }

  if (process.env.NODE_ENV === 'production') {
    handleScriptEqualWeb({
      pathname: location.pathname,
      prevPathname: prevLocation?.pathname,
    });
  }
};

export const wrapRootElement = ({ element }) => {
  /**
   * @type {import('launchdarkly-react-client-sdk').ProviderConfig}
   */
  const launchDarklySettings = {
    clientSideID: process.env.GATSBY_LAUNCHDARKLY_CLIENT_SIDE_ID || 'MISSING_RELEASE_FLAG_KEY',
    options: {
      bootstrap: require('./src/lib/launchDarkly/defaultFlags.js').defaultLDFlags,
    },
    reactOptions: {
      useCamelCaseFlagKeys: false,
    },
  };

  const App = () => {
    useSingularIdentify();
    return <>{element}</>;
  };
  const LDProvider = withLDProvider(launchDarklySettings)(App);

  return <LDProvider />;
};

const setCookieTrackingMain = () => {
  let defaultSearchParams = {};

  if (window?.location?.pathname.includes('/paid')) {
    const os = getMobileOperatingSystem();
    const deviceUtmCampaign = os === 'unknown' ? 'desktop' : os;

    let srcUtmSource = '';
    if (window?.location?.pathname.includes('/paid/google')) {
      srcUtmSource = 'google';
    } else if (window?.location?.pathname.includes('/paid/facebook')) {
      srcUtmSource = 'facebook';
    }

    defaultSearchParams = {
      utm_source: `${srcUtmSource}_mobile_web`,
      utm_campaign: `${deviceUtmCampaign}_mobile_web`,
      wbraid: 'wbraid',
    };
  }

  setCookieTracking('queryParamsSite', defaultSearchParams);
};

export const onPreRouteUpdate = () => {
  if (typeof window === 'undefined' || !window.intellimize) return;
  window.intellimize.ready(() => {
    window.intellimize.activate();
  });
};
